import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import { Clubs } from "../prismaTypes";
import { getString } from "../utils/getString";
import { useNavigate, useParams } from "react-router-dom";
import { queryClient } from "../App";
import { format, parseISO } from "date-fns";
import { LoadingButton } from "@mui/lab";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import { ClubAdmins } from "./ClubAdmins";
import { changeDatesToUTC } from "../utils/dateUtils";
import { uiStore } from "../uiStore";
import { Loading } from "../ui/Loading";

const Input = styled("input")({
  display: "none",
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} {...other}>
      <Box mt={1}>{value === index && children}</Box>
    </div>
  );
}
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const defaultValues = {
  codice: "",
  nome: "",
  inizioAnnoSociale: null,
  indirizzoSede: "",
  localita: "",
  capSede: "",
  provincia: "",
  comune: "",
  telefono: "",
  fax: "",
  emailRiferimento: "",
  urlSitoUfficiale: "",
  urlSitoPrenotazione: "",
  giorniPrenotabiliNelFuturo: null,
  fido: null,
  pagamentoAnticipato: false,
  cardAttive: false,
  // pagamentoPosticipato: false,
  deviceRemoti: false,
  oraMinutiInizioDispRisorse: null,
  oraMinutiFineDispRisorse: null,
  durataSlotMinuti: null,
  numeroMinimoSlot: null,
  annullamentoPrenotazioneMinuti: null,
  tariffaDefaultSlot: null,
  // creazioneAutomaticaCard: false,
  // creazioneAutomaticaConto: false,
  oltreFidoPerAmministratore: false,
  inviaAvvisoAnnullamento: false,
  anticipoAccensioneBottoni: 0,
  attivo: false,
  dataAttivazione: null,
  dataScadenza: null,
  dominio: "",
  luciAutomaticoSeTariffa: false,
  riscaldamentoAutomaticoSeTariffa: false,
  logo: "",
  emailPrenotazioni: "",
  certificatoObbligatorio: false,
  visGiornaliera: false,
};
type Inputs = {
  id?: number;
  codice: string;
  nome: string;
  inizioAnnoSociale: Date | string | null;
  indirizzoSede: string;
  localita: string;
  capSede: string;
  provincia: string;
  comune: string;
  telefono: string;
  fax: string;
  emailRiferimento: string;
  urlSitoUfficiale: string;
  urlSitoPrenotazione: string;
  giorniPrenotabiliNelFuturo: number | null;
  fido: number | null;
  pagamentoAnticipato: boolean;
  cardAttive: boolean;
  // pagamentoPosticipato: boolean;
  deviceRemoti: boolean;
  oraMinutiInizioDispRisorse: string | null;
  oraMinutiFineDispRisorse: string | null;
  durataSlotMinuti: number | null;
  numeroMinimoSlot: number | null;
  annullamentoPrenotazioneMinuti: number | null;
  tariffaDefaultSlot: number | null;
  // creazioneAutomaticaCard: boolean;
  // creazioneAutomaticaConto: boolean;
  oltreFidoPerAmministratore: boolean;
  inviaAvvisoAnnullamento: boolean;
  luciAutomaticoSeTariffa: boolean;
  riscaldamentoAutomaticoSeTariffa: boolean;
  anticipoAccensioneBottoni: number;
  attivo: boolean;
  dataAttivazione: Date | null;
  dataScadenza: Date | null;
  logo: string | null;
  dominio: string;
  emailPrenotazioni: string | null;
  certificatoObbligatorio: boolean;
  visGiornaliera: boolean;
};
const schema = yup
  .object({
    certificatoObbligatorio: yup.boolean().required(),
    nome: yup.string().max(250).required(),
    inizioAnnoSociale: yup
      .date()
      .typeError("Data non valida!")
      .nullable()
      .label("Inizio Anno Sociale")
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    giorniPrenotabiliNelFuturo: yup
      .number()
      .label("giorni Prenotabili Nel Futuro")
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(),
    fido: yup
      .number()
      .label("Scoperto accettabile")
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable()
      .required(),
    pagamentoAnticipato: yup.boolean(),
    cardAttive: yup.boolean(),
    // pagamentoPosticipato: yup.boolean(),
    deviceRemoti: yup.boolean(),
    durataSlotMinuti: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable(),
    numeroMinimoSlot: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable(),
    // creazioneAutomaticaCard: yup.boolean(),
    // creazioneAutomaticaConto: yup.boolean(),
    oltreFidoPerAmministratore: yup.boolean(),
    inviaAvvisoAnnullamento: yup.boolean(),
    anticipo: yup.boolean(),
    dataAttivazione: yup
      .date()
      .typeError("Data non valida!")
      .nullable()
      .label("Data Attivazione")
      .required()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    dataScadenza: yup
      .date()
      .typeError("Data non valida!")
      .nullable()
      .label("Data Scadenza")
      .required()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    dominio: yup.string().max(250).required(),
    emailPrenotazioni: yup.string().max(250),
  })
  .required();

export function CreateClub() {
  let { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { refetch } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/get", id],
    () => axios.get(API_URL + "club/get/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data: { club: Clubs }) => {
        const { club } = data;
        setLoaded(true);

        setValue("codice", getString(club.codice));
        setValue("nome", getString(club.nome));
        setValue("inizioAnnoSociale", club.inizioAnnoSociale);
        setValue("indirizzoSede", getString(club.indirizzoSede));
        setValue("localita", getString(club.localita));
        setValue("capSede", getString(club.capSede));
        setValue("provincia", getString(club.provincia));
        setValue("comune", getString(club.comune));
        setValue("telefono", getString(club.telefono));
        setValue("fax", getString(club.fax));
        setValue("emailRiferimento", getString(club.emailRiferimento));
        setValue("urlSitoUfficiale", getString(club.urlSitoUfficiale));
        setValue("giorniPrenotabiliNelFuturo", club.giorniPrenotabiliNelFuturo);
        setValue("fido", club.fido);
        setValue("pagamentoAnticipato", club.pagamentoAnticipato);
        setValue("cardAttive", club.cardAttive);
        // setValue("pagamentoPosticipato", club.pagamentoPosticipato);
        setValue("deviceRemoti", club.deviceRemoti);

        setValue(
          "oraMinutiInizioDispRisorse",
          club.oraMinutiInizioDispRisorse === null
            ? ""
            : format(parseISO(club.oraMinutiInizioDispRisorse.slice(0, -1)), "HH:mm")
        );
        setValue(
          "oraMinutiFineDispRisorse",
          club.oraMinutiFineDispRisorse === null
            ? ""
            : format(parseISO(club.oraMinutiFineDispRisorse.slice(0, -1)), "HH:mm")
        );

        setValue("durataSlotMinuti", club.durataSlotMinuti);
        setValue("numeroMinimoSlot", club.numeroMinimoSlot);
        setValue("annullamentoPrenotazioneMinuti", club.annullamentoPrenotazioneMinuti);
        setValue("tariffaDefaultSlot", club.tariffaDefaultSlot);
        // setValue("creazioneAutomaticaCard", club.creazioneAutomaticaCard);
        // setValue("creazioneAutomaticaConto", club.creazioneAutomaticaConto);
        setValue("oltreFidoPerAmministratore", club.oltreFidoPerAmministratore);
        setValue("inviaAvvisoAnnullamento", club.inviaAvvisoAnnullamento);
        setValue("anticipoAccensioneBottoni", club.anticipoAccensioneBottoni);
        setValue("attivo", club.attivo);
        setValue("dataAttivazione", club.dataAttivazione);
        setValue("dataAttivazione", club.dataAttivazione);
        setValue("dataAttivazione", club.dataAttivazione);
        setValue("dataScadenza", club.dataScadenza);
        setValue("dominio", club.dominio);
        setValue("luciAutomaticoSeTariffa", club.luciAutomaticoSeTariffa);
        setValue("riscaldamentoAutomaticoSeTariffa", club.riscaldamentoAutomaticoSeTariffa);
        setValue("logo", club.logo);
        setValue("urlSitoPrenotazione", club.urlSitoPrenotazione);
        setValue("emailPrenotazioni", club.emailPrenotazioni);
        setValue("certificatoObbligatorio", club.certificatoObbligatorio);
        setValue("visGiornaliera", club.visGiornaliera);
      },
      onError: (error: AxiosError) => {
        console.error(error);
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error, mutate } = useMutation(
    (login: Inputs) => {
      return axios.post(API_URL + "club/create", login);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        const { id } = data.data;
        if (id) {
          enqueueSnackbar("Club Creato", { variant: "success" });
          navigate("/app/editClub/" + id);
        }
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "club/update", { ...data });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Club Aggiornato", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    changeDatesToUTC(data);
    if (id !== undefined) mutateUpdate({ ...data, id: parseInt(id) });
    else mutate(data);
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  const { isLoading, mutate: uploadLogo } = useMutation(
    (data: any) => {
      return axios.post(API_URL + "club/uploadLogo", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess: (resp: AxiosResponse) => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Logo caricato", { variant: "success" });
        setValue("logo", resp.data?.file);
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const handleLogoUpload = (event: any) => {
    const data = new FormData();
    data.append("file", event.target.files[0]);
    uploadLogo(data);
  };

  const [anaOpne, setAnaOpen] = useState(false);

  const {
    isLoading: isLoading2,
    mutate: uploadAnagrafiche,
    data: anaData,
  } = useMutation(
    (data: any) => {
      return axios.post(API_URL + "users/importExcel", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess: (
        resp: AxiosResponse<{ result: { errori: string[]; creati: string[]; saltati: string[] } }>
      ) => {
        queryClient.invalidateQueries();
        setAnaOpen(true);
        enqueueSnackbar("Anagrafiche caricate", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        setAnaOpen(true);
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );
  const handleAnagraficheUpload = (event: any) => {
    const data = new FormData();
    data.append("file", event.target.files[0]);
    data.append("clubId", id ?? "");
    uploadAnagrafiche(data);
  };

  let errorMsg = "";
  if (error instanceof Error) errorMsg = error.message;

  const title =
    id === undefined ? "Crea Nuovo Club" : getValues("codice") + " " + getValues("nome");

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  if (!loaded) return <Loading />;

  return (
    <Box className={animations ? "" : "noAnimation"}>
      {/* <Title title={title} /> */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs value={tabValue} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Dati di Base" {...a11yProps(0)} />
          <Tab label="Contatti ed Indirizzi" {...a11yProps(1)} />
          <Tab label="Impostazioni" {...a11yProps(2)} />
          <Tab label="Amministrazione" {...a11yProps(3)} />
          <Tab label="Amministratori" {...a11yProps(4)} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container columnSpacing={1}>
            <Grid item xs={12} md={4} lg={2}>
              <Controller
                name="codice"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Codice"
                    error={Boolean(errors.codice)}
                    helperText={errors.codice?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={5}>
              <Controller
                name="nome"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="standard"
                    label="Denominazione"
                    error={Boolean(errors.nome)}
                    helperText={errors.nome?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={5}>
              <Controller
                name="dominio"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="standard"
                    label="Dominio"
                    error={Boolean(errors.dominio)}
                    helperText={errors.dominio?.message ?? " "}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={4}>
              <Controller
                name="inizioAnnoSociale"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    mask={"__/__/____"}
                    label="Inizio AnnoSociale"
                    onChange={field.onChange}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        helperText={errors.inizioAnnoSociale?.message ?? " "}
                        {...params}
                        error={Boolean(errors.inizioAnnoSociale)}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {getValues("logo") && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <img
                    src={API_URL + "/loghi/" + getValues("logo")}
                    crossOrigin="anonymous"
                    alt="logo"
                    width="250px"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Controller
                name="indirizzoSede"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Indirizzo"
                    error={Boolean(errors.indirizzoSede)}
                    helperText={errors.indirizzoSede?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="localita"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Localita"
                    error={Boolean(errors.localita)}
                    helperText={errors.localita?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Controller
                name="capSede"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Cap"
                    error={Boolean(errors.capSede)}
                    helperText={errors.capSede?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="provincia"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Provincia"
                    error={Boolean(errors.provincia)}
                    helperText={errors.provincia?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="comune"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Comune"
                    error={Boolean(errors.comune)}
                    helperText={errors.comune?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="emailRiferimento"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Email Riferimento"
                    error={Boolean(errors.emailRiferimento)}
                    helperText={errors.emailRiferimento?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="emailPrenotazioni"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Email Prenotazioni"
                    error={Boolean(errors.emailPrenotazioni)}
                    helperText={errors.emailPrenotazioni?.message ?? " "}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12} md={6}>
              <Controller
                name="urlSitoUfficiale"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="URL Sito Ufficiale"
                    error={Boolean(errors.urlSitoUfficiale)}
                    helperText={errors.urlSitoUfficiale?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="urlSitoPrenotazione"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="URL Sito Prenotazione"
                    required
                    error={Boolean(errors.urlSitoPrenotazione)}
                    helperText={errors.urlSitoPrenotazione?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="telefono"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Telefono"
                    error={Boolean(errors.telefono)}
                    helperText={errors.telefono?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="fax"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Fax"
                    error={Boolean(errors.fax)}
                    helperText={errors.fax?.message ?? " "}
                  />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container>
            <Grid item xs={12} md={6} lg={4}>
              <Controller
                name="giorniPrenotabiliNelFuturo"
                control={control}
                defaultValue={30}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Giorni futuri per prenotazione"
                    type="number"
                    error={Boolean(errors.giorniPrenotabiliNelFuturo)}
                    helperText={errors.giorniPrenotabiliNelFuturo?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Controller
                name="fido"
                control={control}
                defaultValue={30}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Scoperto accettabile (fido)"
                    type="number"
                    error={Boolean(errors.fido)}
                    helperText={errors.fido?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="pagamentoAnticipato"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("pagamentoAnticipato", v.target.checked)}
                      />
                    }
                    label="Attivazione pagamento anticipato"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="deviceRemoti"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("deviceRemoti", v.target.checked)}
                      />
                    }
                    label="Utilizzo device remoti"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="cardAttive"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("cardAttive", v.target.checked)}
                      />
                    }
                    label="Attivazione card"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="certificatoObbligatorio"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("certificatoObbligatorio", v.target.checked)}
                      />
                    }
                    label="Certificato Obbligatorio"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel component="legend" sx={{ mt: 1 }}>
                Ora Default disponibilità risorse
              </FormLabel>
              <Controller
                name="oraMinutiInizioDispRisorse"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: 150, mt: 1, mr: 2 }}
                    label="Inizio"
                    type="time"
                    error={Boolean(errors.oraMinutiInizioDispRisorse)}
                    helperText={errors.oraMinutiInizioDispRisorse?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="oraMinutiFineDispRisorse"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: 150, mt: 1 }}
                    label="Fine"
                    type="time"
                    error={Boolean(errors.oraMinutiFineDispRisorse)}
                    helperText={errors.oraMinutiFineDispRisorse?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel component="legend" sx={{ mt: 1 }}>
                Slots
              </FormLabel>
              <Controller
                name="durataSlotMinuti"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150, mr: 2 }}
                    label="Durata Minuti"
                    type="number"
                    error={Boolean(errors.durataSlotMinuti)}
                    helperText={errors.durataSlotMinuti?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="numeroMinimoSlot"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150, mr: 2 }}
                    label="Numero Minimo"
                    type="number"
                    error={Boolean(errors.numeroMinimoSlot)}
                    helperText={errors.numeroMinimoSlot?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="tariffaDefaultSlot"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150 }}
                    label="Costo Default"
                    type="number"
                    error={Boolean(errors.tariffaDefaultSlot)}
                    helperText={errors.tariffaDefaultSlot?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel component="legend" sx={{ mt: 1 }}>
                Periodo annullamento
              </FormLabel>
              <Controller
                name="annullamentoPrenotazioneMinuti"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150 }}
                    label="Minuti"
                    type="number"
                    error={Boolean(errors.annullamentoPrenotazioneMinuti)}
                    helperText={errors.annullamentoPrenotazioneMinuti?.message ?? " "}
                  />
                )}
              />
            </Grid>
            {/* <Grid item xs={12} lg={6}>
              <Controller
                name="creazioneAutomaticaCard"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("creazioneAutomaticaCard", v.target.checked)}
                      />
                    }
                    label="Attivazione creazione automatica card"
                  />
                )}
              />
            </Grid> */}
            {/* <Grid item xs={12} lg={6}>
              <Controller
                name="creazioneAutomaticaConto"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("creazioneAutomaticaConto", v.target.checked)}
                      />
                    }
                    label="Attivazione creazione automatica conto"
                  />
                )}
              />
            </Grid> */}
            <Grid item xs={12} lg={6}>
              <Controller
                name="oltreFidoPerAmministratore"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("oltreFidoPerAmministratore", v.target.checked)}
                      />
                    }
                    label="L'amministratore può prenotare anche con supero del fido"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="inviaAvvisoAnnullamento"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("inviaAvvisoAnnullamento", v.target.checked)}
                      />
                    }
                    label="Inviare email in caso di annullamento"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="luciAutomaticoSeTariffa"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("luciAutomaticoSeTariffa", v.target.checked)}
                      />
                    }
                    label="Luci automatiche se tariffa"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="visGiornaliera"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("visGiornaliera", v.target.checked)}
                      />
                    }
                    label="Visualizzazione giornaliera"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="riscaldamentoAutomaticoSeTariffa"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) =>
                          setValue("riscaldamentoAutomaticoSeTariffa", v.target.checked)
                        }
                      />
                    }
                    label="Riscaldamento Automatico se tariffa"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel component="legend" sx={{ mt: 1 }}>
                Anticipo visualizzazione pulsanti per luci etc
              </FormLabel>
              <Controller
                name="anticipoAccensioneBottoni"
                control={control}
                defaultValue={30}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Minuti"
                    type="number"
                    sx={{ width: 150 }}
                    error={Boolean(errors.anticipoAccensioneBottoni)}
                    helperText={errors.anticipoAccensioneBottoni?.message ?? " "}
                  />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container>
            <Grid item xs={12}>
              <Controller
                name="attivo"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("attivo", v.target.checked)}
                      />
                    }
                    label="Attivazione"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="dataAttivazione"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    mask={"__/__/____"}
                    label="Data Attivazione"
                    onChange={field.onChange}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        sx={{ mr: 2 }}
                        variant="standard"
                        helperText={errors.dataAttivazione?.message ?? " "}
                        {...params}
                        error={Boolean(errors.dataAttivazione)}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="dataScadenza"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    mask={"__/__/____"}
                    label="Data Scadenza"
                    onChange={field.onChange}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        helperText={errors.dataScadenza?.message ?? " "}
                        {...params}
                        error={Boolean(errors.dataScadenza)}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {id && <ClubAdmins clubId={id} />}
        </TabPanel>

        {!id && (
          <Button type="submit" variant="contained" sx={{ mt: 2 }}>
            Crea Club
          </Button>
        )}
        {id && (
          <Button type="submit" variant="contained" sx={{ mt: 2 }}>
            Modifica Club
          </Button>
        )}

        {id && (
          <>
            <label htmlFor="upload-button-file">
              <Input id="upload-button-file" multiple type="file" onChange={handleLogoUpload} />
              <LoadingButton
                sx={{ mt: 2, ml: 2 }}
                variant="contained"
                color="secondary"
                component="span"
                loading={isLoading}
                startIcon={<CloudUploadIcon />}
              >
                Carica Logo
              </LoadingButton>
            </label>

            <label htmlFor="upload-button-file2">
              <Input
                id="upload-button-file2"
                multiple
                type="file"
                onChange={handleAnagraficheUpload}
              />
              <LoadingButton
                sx={{ mt: 2, ml: 2 }}
                variant="contained"
                color="secondary"
                component="span"
                loading={isLoading2}
                startIcon={<CloudUploadIcon />}
              >
                Carica Anagrafiche XLS
              </LoadingButton>
            </label>
          </>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMsg}
          </Alert>
        )}
      </form>

      <Dialog open={anaOpne} onClose={() => setAnaOpen(false)}>
        <DialogTitle>Risultato importazione</DialogTitle>
        <DialogContent>
          Creati:
          <ul>
            {anaData?.data?.result?.creati.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          Saltati:
          <ul>
            {anaData?.data?.result?.saltati.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          Errori:
          <ul>
            {anaData?.data?.result?.errori.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnaOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
